import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {
  ReviewStatus,
  ReviewLifecycleTerminalState,
  ReviewerStatus,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import ReviewConstants, {Review} from 'notebook/reviews/ReviewConstants';
import {ReviewFilter} from 'search/SearchConstants';
import Button, {ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';
import pickDefined from 'util/pickDefined';

import {BASE_FILTER_FORM_PROP_NAMES, BaseFilterFormProps, FilterStyle} from './base/FilterFormConstants';
import SecondaryFilterFormSkeleton from './base/SecondaryFilterFormSkeleton';

export const ANY_STATUS = 'ANY_STATUS';
export const ANY_REVIEWER_STATUS = 'ANY_REVIEWER_STATUS';
const ANY_TERMINAL_STATE = 'ANY_TERMINAL_STATE';
export const NO_ROLE = 'NO_ROLE';
export const DEFAULT_ROLE = ReviewConstants.ROLE_FILTERS.AUDITOR;

const REVIEW_STATUSES = {
  [ANY_STATUS]: 'Any status',
  [ReviewConstants.IN_PROGRESS_FILTER]: 'In Progress',
  [ReviewStatus.NEEDS_REVIEW]: 'Needs Review',
  [ReviewStatus.ACCEPTED]: 'Accepted',
  [ReviewStatus.REJECTED]: 'Needs Revision',
  // not implemented in menu
  [ReviewStatus.ACCEPTANCE_SNAPSHOT_IN_PROGRESS]: null,
  [ReviewStatus.ACTION_REQUIRED]: null,
  [ReviewStatus.RETRACTED]: null,
  [ReviewStatus.REVIEW_SNAPSHOT_IN_PROGRESS]: null,
};

const REVIEWER_STATUSES = {
  [ANY_REVIEWER_STATUS]: 'Needs Review',
  [ReviewerStatus.PENDING]: 'Ready for Review',
  [ReviewerStatus.BLOCKED]: 'Awaiting Prior Review',
  // not implemented in submenu
  [ReviewerStatus.FINISHED]: null,
  [ReviewerStatus.REJECTED]: null,
};

const TERMINAL_STATUSES = {
  [ANY_TERMINAL_STATE]: 'Any Completion Status',
  [ReviewLifecycleTerminalState.ACCEPTED]: 'Accepted',
  [ReviewLifecycleTerminalState.APPROVED]: 'Approved',
  [ReviewLifecycleTerminalState.COMPLETED]: 'Completed',
  [ReviewLifecycleTerminalState.REVIEWED]: 'Reviewed',
  [ReviewLifecycleTerminalState.WITNESSED]: 'Witnessed',
  [ReviewLifecycleTerminalState.SELF_REVIEWED]: 'Self-reviewed',
};

interface ReviewFilterFormProps extends BaseFilterFormProps {
  filter: ReviewFilter;
  filterStyle: FilterStyle;
  onChangeFilter: (filter: ReviewFilter) => void;
  onRemove: () => void;
}

export interface ReviewFilterFormExtraProps {
  review: Review;
}

class ReviewFilterForm extends React.Component<ReviewFilterFormProps & ReviewFilterFormExtraProps> {
  REVIEW_STATUS_OPTIONS = [
    {name: REVIEW_STATUSES[ANY_STATUS], value: ANY_STATUS},
    {
      name: REVIEW_STATUSES[ReviewConstants.IN_PROGRESS_FILTER],
      value: ReviewConstants.IN_PROGRESS_FILTER,
    },
    {name: REVIEW_STATUSES[ReviewStatus.NEEDS_REVIEW], value: ReviewStatus.NEEDS_REVIEW},
    ...(BenchlingConfig.ENABLE_ENTRY_REVIEW_PROCESSES
      ? []
      : [{name: REVIEW_STATUSES[ReviewStatus.ACCEPTED], value: ReviewStatus.ACCEPTED}]),
    {name: REVIEW_STATUSES[ReviewStatus.REJECTED], value: ReviewStatus.REJECTED},
  ];
  reviewerOrAuditor = BenchlingConfig.ENABLE_ENTRY_REVIEW_PROCESSES ? 'a reviewer' : 'an auditor';
  DEFAULT_ROLE_OPTION = {
    name: `where I am ${this.reviewerOrAuditor}`,
    value: ReviewConstants.ROLE_FILTERS.AUDITOR,
  };
  ROLE_OPTIONS = [
    this.DEFAULT_ROLE_OPTION,
    {name: 'where I am an author', value: ReviewConstants.ROLE_FILTERS.AUTHOR},
    {
      name: `where I am an author or ${this.reviewerOrAuditor}`,
      value: ReviewConstants.ROLE_FILTERS.AUTHOR_OR_AUDITOR,
    },
    {name: `where anyone is an author or ${this.reviewerOrAuditor}`, value: NO_ROLE},
  ];
  REVIEWER_STATUS_OPTIONS = [
    {
      key: 'section-1',
      optionDatas: [{name: REVIEWER_STATUSES[ANY_REVIEWER_STATUS], value: ANY_REVIEWER_STATUS}],
    },
    {
      key: 'section-2',
      optionDatas: [
        {name: REVIEWER_STATUSES[ReviewerStatus.PENDING], value: ReviewerStatus.PENDING},
        {name: REVIEWER_STATUSES[ReviewerStatus.BLOCKED], value: ReviewerStatus.BLOCKED},
      ],
    },
  ];
  TERMINAL_STATUS_OPTIONS = [
    {
      key: 'section-1',
      optionDatas: [{name: TERMINAL_STATUSES[ANY_TERMINAL_STATE], value: ANY_TERMINAL_STATE}],
    },
    {
      key: 'section-2',
      optionDatas: [
        {
          name: TERMINAL_STATUSES[ReviewLifecycleTerminalState.ACCEPTED],
          value: ReviewLifecycleTerminalState.ACCEPTED,
        },
        {
          name: TERMINAL_STATUSES[ReviewLifecycleTerminalState.APPROVED],
          value: ReviewLifecycleTerminalState.APPROVED,
        },
        {
          name: TERMINAL_STATUSES[ReviewLifecycleTerminalState.COMPLETED],
          value: ReviewLifecycleTerminalState.COMPLETED,
        },
        {
          name: TERMINAL_STATUSES[ReviewLifecycleTerminalState.REVIEWED],
          value: ReviewLifecycleTerminalState.REVIEWED,
        },
        {
          name: TERMINAL_STATUSES[ReviewLifecycleTerminalState.WITNESSED],
          value: ReviewLifecycleTerminalState.WITNESSED,
        },
        {
          name: TERMINAL_STATUSES[ReviewLifecycleTerminalState.SELF_REVIEWED],
          value: ReviewLifecycleTerminalState.SELF_REVIEWED,
        },
      ],
    },
  ];

  constructor(props: ReviewFilterFormProps & ReviewFilterFormExtraProps) {
    super(props);
    if (props.filter.value.role == null) {
      props.filter.value.role = DEFAULT_ROLE;
    }
  }

  _getDisplayName(
    reviewStatus: ReviewStatus | typeof ReviewConstants.IN_PROGRESS_FILTER | typeof ANY_STATUS,
    reviewerStatus: ReviewerStatus | typeof ANY_REVIEWER_STATUS,
    terminalState: ReviewLifecycleTerminalState | typeof ANY_TERMINAL_STATE
  ): string {
    if (BenchlingConfig.ENABLE_ENTRY_REVIEW_PROCESSES && reviewStatus === ReviewStatus.ACCEPTED) {
      return TERMINAL_STATUSES[terminalState];
    }
    if (reviewStatus === ReviewStatus.NEEDS_REVIEW) {
      return REVIEWER_STATUSES[reviewerStatus] ?? 'Any status';
    }
    return REVIEW_STATUSES[reviewStatus] ?? 'Any status';
  }

  _handleSelectStatus = (
    selectedStatus: ReviewStatus | typeof ReviewConstants.IN_PROGRESS_FILTER | typeof ANY_STATUS
  ): void => {
    let status: string | null = selectedStatus;
    if (status === ANY_STATUS) {
      status = null;
    }
    this.props.onChangeFilter({
      ...this.props.filter,
      value: {...this.props.filter.value, review_status: status, reviewerStatus: null, terminalState: null},
    });
  };

  _handleSelectRole = (selectedRole: string): void => {
    const newFilterValue = {...this.props.filter.value, role: selectedRole};
    // if toggle away from "where I am reviewer" role, reset reviewerStatus submenu
    if (
      this.props.filter.value.review_status === ReviewStatus.NEEDS_REVIEW &&
      selectedRole !== ReviewConstants.ROLE_FILTERS.AUDITOR
    ) {
      newFilterValue.reviewerStatus = null;
      newFilterValue.review_status = ReviewStatus.NEEDS_REVIEW;
    }
    this.props.onChangeFilter({
      ...this.props.filter,
      value: newFilterValue,
    });
  };

  _handleSelectReviewerStatus = (
    selectedReviewerStatus: ReviewerStatus | typeof ANY_REVIEWER_STATUS
  ): void => {
    this.props.onChangeFilter({
      ...this.props.filter,
      value: {
        ...this.props.filter.value,
        review_status: ReviewStatus.NEEDS_REVIEW,
        reviewerStatus: selectedReviewerStatus === ANY_REVIEWER_STATUS ? null : selectedReviewerStatus,
        terminalState: null,
      },
    });
  };

  _handleSelectCompletionStatus = (
    selectedTerminalState: ReviewLifecycleTerminalState | typeof ANY_TERMINAL_STATE
  ): void => {
    let terminalState: ReviewLifecycleTerminalState | typeof ANY_TERMINAL_STATE | null =
      selectedTerminalState;
    if (terminalState === ANY_TERMINAL_STATE) {
      terminalState = null;
    }
    this.props.onChangeFilter({
      ...this.props.filter,
      value: {
        ...this.props.filter.value,
        review_status: ReviewStatus.ACCEPTED,
        terminalState,
      },
    });
  };

  render(): JSX.Element | null {
    if (this.props.filterStyle !== FilterStyle.SECONDARY) {
      throw new Error('ReviewFilterForm not yet implemented as primary filter');
    }
    const skeletonProps = pickDefined(this.props, ...BASE_FILTER_FORM_PROP_NAMES);
    const selectedReviewStatus = this.props.filter.value?.review_status ?? ANY_STATUS;
    const selectedReviewerStatus = this.props.filter.value?.reviewerStatus ?? ANY_REVIEWER_STATUS;
    const selectedTerminalState = this.props.filter.value?.terminalState ?? ANY_TERMINAL_STATE;
    const selectedRole = this.props.filter.value?.role ?? DEFAULT_ROLE;
    const selectedRoleDisplayName = (
      this.ROLE_OPTIONS.find((roleOptionData) => roleOptionData.value === selectedRole) ??
      this.DEFAULT_ROLE_OPTION
    ).name;
    const reviewStatusDisplayName = this._getDisplayName(
      selectedReviewStatus,
      selectedReviewerStatus,
      selectedTerminalState
    );

    const reviewerStatusSubMenu = (
      <Menu height={MenuHeight.MEDIUM} width={MenuWidth.FULL} value={selectedReviewerStatus}>
        {this.REVIEWER_STATUS_OPTIONS.map((section) => (
          <Section key={section.key}>
            {section.optionDatas.map((optionData) => (
              <Option
                key={optionData.value}
                onSelect={this._handleSelectReviewerStatus}
                shouldEllipsize={true}
                value={optionData.value}
                dataTestAttrs={{
                  component: 'ReviewFilterForm',
                  element: 'reviewStatusOption',
                  key: optionData.value,
                }}
              >
                {optionData.name === REVIEWER_STATUSES[ANY_REVIEWER_STATUS] ? 'Any' : optionData.name}
              </Option>
            ))}
          </Section>
        ))}
      </Menu>
    );
    const terminalStatusSubMenu = (
      <Menu height={MenuHeight.MEDIUM} width={MenuWidth.FULL} value={selectedTerminalState}>
        {this.TERMINAL_STATUS_OPTIONS.map((section) => (
          <Section key={section.key}>
            {section.optionDatas.map((optionData) => (
              <Option
                key={optionData.value}
                onSelect={this._handleSelectCompletionStatus}
                shouldEllipsize={true}
                value={optionData.value}
                dataTestAttrs={{
                  component: 'ReviewFilterForm',
                  element: 'reviewStatusOption',
                  key: optionData.value,
                }}
              >
                {optionData.name}
              </Option>
            ))}
          </Section>
        ))}
      </Menu>
    );
    const reviewStatusDropdown = (
      <Dropdown
        closeOnInsideClick={true}
        menu={
          <Menu height={MenuHeight.MEDIUM} width={MenuWidth.FULL} value={selectedReviewStatus}>
            {this.REVIEW_STATUS_OPTIONS.map((optionData) => (
              <Option
                key={optionData.value}
                onSelect={this._handleSelectStatus}
                shouldEllipsize={true}
                value={optionData.value}
                dataTestAttrs={{
                  component: 'ReviewFilterForm',
                  element: 'reviewStatusOption',
                  key: optionData.value,
                }}
              >
                {optionData.name}
                {optionData.value === ReviewStatus.NEEDS_REVIEW &&
                  selectedRole === ReviewConstants.ROLE_FILTERS.AUDITOR &&
                  reviewerStatusSubMenu}
              </Option>
            ))}
            {BenchlingConfig.ENABLE_ENTRY_REVIEW_PROCESSES && (
              <Option
                key="Completion Status"
                onSelect={this._handleSelectStatus}
                value={ReviewStatus.ACCEPTED}
                dataTestAttrs={{
                  component: 'ReviewFilterForm',
                  element: 'reviewStatusOption',
                  key: ReviewStatus.ACCEPTED,
                }}
              >
                Completion Status
                {terminalStatusSubMenu}
              </Option>
            )}
          </Menu>
        }
      >
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          fullWidth={true}
          isDropdown={true}
          size={this.props.buttonSize}
          text={reviewStatusDisplayName}
          disabled={this.props.isDisabled}
          dataTestAttrs={{
            component: 'ReviewFilterForm',
            element: 'reviewStatusDropdown',
          }}
        />
      </Dropdown>
    );
    const roleDropdown = (
      <Dropdown
        closeOnInsideClick={true}
        menu={
          <Menu height={MenuHeight.MEDIUM} value={selectedRole} width={MenuWidth.FULL}>
            {this.ROLE_OPTIONS.map((optionData) => (
              <Option
                key={optionData.name || optionData.value}
                onSelect={this._handleSelectRole}
                shouldEllipsize={true}
                value={optionData.value}
              >
                {optionData.name}
              </Option>
            ))}
          </Menu>
        }
      >
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          fullWidth={true}
          isDropdown={true}
          size={this.props.buttonSize}
          text={selectedRoleDisplayName}
          disabled={this.props.isDisabled}
        />
      </Dropdown>
    );
    return (
      <SecondaryFilterFormSkeleton
        {...skeletonProps}
        nodeGrid={[[reviewStatusDropdown], [roleDropdown]]}
        onChangeFilterKey={this.props.onChangeFilterKey}
        selectedFilterKey={this.props.selectedFilterKey}
        onRemove={this.props.onRemove}
      />
    );
  }
}

export default ReviewFilterForm;
